"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { platformModules } from "@/lib/data/platform";
import { cn } from "@/lib/utils";
import { KpiMonitor } from "@/components/platform/modules/kpi-monitor";
import { ActionPlan } from "@/components/platform/modules/action-plan";
import { BudgetMonitor } from "@/components/platform/modules/budget-monitor";
import { ExecutiveSummary } from "@/components/platform/modules/executive-summary";

const moduleUIs = {
  detect: KpiMonitor,
  decide: ActionPlan,
  scale: BudgetMonitor,
  explain: ExecutiveSummary,
} as const;

type ModuleKey = keyof typeof moduleUIs;

export function ProductShell({
  initialId = "detect",
  autoRotate = false,
  className,
}: {
  initialId?: string;
  autoRotate?: boolean;
  className?: string;
}) {
  const [activeId, setActiveId] = useState<string>(initialId);
  const [changing, setChanging] = useState(false);
  const hoverPaused = useRef(false);
  const activeIndex = useMemo(() => platformModules.findIndex((m) => m.id === activeId), [activeId]);
  const ActiveUI = moduleUIs[activeId as ModuleKey] ?? KpiMonitor;
  const activeModule = platformModules[activeIndex] ?? platformModules[0];

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!autoRotate || prefersReduced) return;
    const timer = window.setInterval(() => {
      if (hoverPaused.current) return;
      const next = platformModules[(activeIndex + 1) % platformModules.length];
      setChanging(true);
      window.setTimeout(() => {
        setActiveId(next.id);
        setChanging(false);
      }, 180);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [autoRotate, activeIndex]);

  const select = (id: string) => {
    if (id === activeId) return;
    setChanging(true);
    window.setTimeout(() => {
      setActiveId(id);
      setChanging(false);
    }, 140);
  };

  return (
    <div className={cn("platform-shell", className)}>
      <div
        className="platform-tabs"
        role="tablist"
        aria-label="Platform capabilities"
        onPointerEnter={() => {
          hoverPaused.current = true;
        }}
        onPointerLeave={() => {
          hoverPaused.current = false;
        }}
      >
        {platformModules.map((m, index) => (
          <button
            key={m.id}
            type="button"
            role="tab"
            id={`platform-tab-${m.id}`}
            aria-selected={m.id === activeId}
            aria-controls={`platform-panel-${m.id}`}
            className={cn("platform-tab", m.id === activeId && "active")}
            onClick={() => select(m.id)}
          >
            <span>0{index + 1}</span> {m.kicker.split(" ")[0]}
          </button>
        ))}
      </div>

      <div className="platform-stage">
        <div className="platform-copy" id={`platform-panel-${activeId}`} role="tabpanel" aria-labelledby={`platform-tab-${activeId}`}>
          <p className="platform-kicker">{activeModule.kicker}</p>
          <h3>{activeModule.title}</h3>
          <p>{activeModule.description}</p>
          <ul>
            {activeModule.bullets.map((bullet) => (
              <li key={bullet}>
                <span aria-hidden="true">✓</span> {bullet}
              </li>
            ))}
          </ul>
        </div>

        <div className="product-window" role="region" aria-label="Interactive DBM product preview">
          <div className="product-window-bar">
            <span className="product-window-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <strong>{activeModule.screenTitle}</strong>
            <span className="product-live">
              <i aria-hidden="true" /> Live preview
            </span>
          </div>
          <div className={cn("product-surface", changing && "is-changing")} aria-live="polite">
            <ActiveUI />
          </div>
          <div className="product-window-footer">
            <span>{activeModule.screenDetail}</span>
            <small>Interactive preview · no live account data</small>
          </div>
        </div>
      </div>
    </div>
  );
}